<script lang="ts" setup>
import { VdForm, VdFormField } from "@voodoo.io/voodoo-ui";
import { reactive, ref } from "vue";
import { useRouter } from "vue-router";

import { queryNames, useMutationResponse, usePublishPagePublishVersionMutation } from "@/graphql";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import useRandomLoadingSentences from "@/hooks/random-loading-sentences";
import usePublicationStatus from "@/hooks/usePublicationStatus";
import links from "@/router/links";

import rocket from "./assets/rocket.svg";
import ActionLogsReview from "./components/ActionLogsReview.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const publishDone = ref(false);
const publishError = ref("");
const form = reactive({
  name: "",
});

const actionLogs = ref([]);

const { flags } = useFeatureFlags(props.gameId);
const { mutate, loading: saveLoading } = usePublishPagePublishVersionMutation({
  refetchQueries: [queryNames.GameDisplayPage],
});
const { start: startSentencesRoll, stop: stopSentencesRoll, sentence: waitSentence } = useRandomLoadingSentences();
const { refetch: refetchPublicationStatus } = usePublicationStatus(props.gameId);

const publishedVersionId = ref("");

const saveForm = async () => {
  startSentencesRoll();
  const response = await mutate({
    gameId: props.gameId,
    name: form.name,
    // Multi-link: linked offers follow their own status lifecycle at publish instead of
    // mirroring their test's state
    useMultiLink: flags.value.offerMultiLinkABTest.enabled,
  });

  const data = useMutationResponse(response, (resp) => resp?.publishVersion, { shouldNotifyError: false });

  if (data?.success) {
    publishedVersionId.value = data?.versionId ?? "";
  } else if (data?.isConflict) {
    publishError.value = data.errorMessage ?? "";
  } else {
    publishError.value = "Your publish has failed";
  }

  publishDone.value = true;
  stopSentencesRoll();
  refetchPublicationStatus();
};

const resetForm = () => {
  publishDone.value = false;
  publishError.value = "";
};

const goToVersion = (versionId: string) => {
  router.push({ name: links.games.home, params: { gameId: props.gameId, versionId } });
};

const goToWIPVersion = () => goToVersion("wip");
const goToLiveVersion = () => goToVersion(publishedVersionId.value);

/**
 * The probability that someone hit the publish button is
 * way higher than the probability of getting here from a link.
 */
const onClose = () => {
  if (!saveLoading.value) {
    router.back();
  }

  // router.push({ name: links.games.home, params: { gameId: props.gameId, versionId: props.versionId } });
};
</script>

<template>
  <vd-dialog visible class="game-version-publish" :close-on-click-outside="!saveLoading" @close="onClose">
    <img :src="rocket" class="rocket" />

    <div v-if="saveLoading" class="publish-wait">
      <p>Please wait while your config is published</p>
      <p v-html="waitSentence"></p>
    </div>

    <template v-else-if="publishDone">
      <div v-if="publishError" class="publish-error">
        <p>Something happened !</p>
        <p class="error" v-html="publishError"></p>
        <div class="actions">
          <vd-button size="large" @click="onClose">Close</vd-button>
          <vd-button size="large" @click="resetForm">Try again</vd-button>
        </div>
      </div>
      <div v-else class="publish-success">
        <p>Your config was published with success.</p>
        <div class="actions">
          <vd-button size="large" @click="goToLiveVersion">
            <vd-custom-icon name="live-icon" />
            Go to live published config
          </vd-button>
          <vd-button size="large" @click="goToWIPVersion">
            <vd-custom-icon name="traffic-plot-icon" />
            Go to new wip config
          </vd-button>
        </div>
      </div>
    </template>

    <div v-else class="publish-form">
      <h3>Are you sure you want to publish this version ?</h3>
      <p>Be sure that all settings are correct before going through this process…</p>
      <ActionLogsReview v-model:action-logs="actionLogs" :game-id="gameId" />
      <VdForm :form="form" size="large" save-button-label="Publish" @submit="saveForm" @cancel="onClose">
        <VdFormField
          v-model="form.name"
          prop="name"
          type="text"
          label="New version name"
          placeholder="Enter new version name here before publishing"
          clearable
          required
          @keydown.enter.prevent="(e) => e.preventDefault()"
        />
      </VdForm>
    </div>
  </vd-dialog>
</template>

<style lang="scss">
.game-version-publish {
  p {
    text-align: center;
    word-break: normal;
  }

  .error {
    color: $red-alert;
  }

  .rocket {
    width: 100px;
    margin: size("XL") calc(50% - 50px);
  }

  .publish-form {
    text-align: center;

    h3 {
      margin-bottom: size("XS");
      color: var(--vd-base-primary-color);
      font-weight: bold;
    }

    p {
      margin-top: 0;
    }
  }

  .actions {
    text-align: center;
  }

  .anim-load {
    display: inline-block;
    font-style: italic;
    text-align: center;
    animation: appear-load 2s ease-in-out;
  }
}
</style>
